
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import FirstNameStep from './steps/FirstNameStep';
import LastNameStep from './steps/LastNameStep';
import DateOfBirthStep from './steps/DateOfBirthStep';
import IntensityStep from './steps/IntensityStep';
import SocialIntegrationStep from './steps/SocialIntegrationStep';
import WelcomeStep from './steps/WelcomeStep';

export type OnboardingData = {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  intensity: 'light' | 'medium' | 'intense';
  socialLogin?: 'google' | 'apple' | 'facebook' | 'skip';
};

const OnboardingFlow = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [data, setData] = useState<OnboardingData>({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    intensity: 'medium',
  });
  const navigate = useNavigate();

  const updateData = (newData: Partial<OnboardingData>) => {
    setData(prev => ({ ...prev, ...newData }));
  };

  const nextStep = () => {
    setCurrentStep(prev => prev + 1);
  };

  const prevStep = () => {
    setCurrentStep(prev => prev - 1);
  };

  const completeOnboarding = () => {
    // Store user data in localStorage or send to backend
    localStorage.setItem('showUpUser', JSON.stringify(data));
    localStorage.setItem('hasCompletedOnboarding', 'true');
    navigate('/app');
  };

  const steps = [
    <FirstNameStep key="firstName" data={data} updateData={updateData} onNext={nextStep} />,
    <LastNameStep key="lastName" data={data} updateData={updateData} onNext={nextStep} onBack={prevStep} />,
    <DateOfBirthStep key="dateOfBirth" data={data} updateData={updateData} onNext={nextStep} onBack={prevStep} />,
    <IntensityStep key="intensity" data={data} updateData={updateData} onNext={nextStep} onBack={prevStep} />,
    <SocialIntegrationStep key="social" data={data} updateData={updateData} onNext={nextStep} onBack={prevStep} />,
    <WelcomeStep key="welcome" data={data} onComplete={completeOnboarding} onBack={prevStep} />
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-soft via-blue-whisper to-white flex items-center justify-center px-4 py-6">
      <div className="w-full max-w-sm mx-auto">
        {/* Progress Indicator */}
        <div className="mb-8">
          <div className="flex justify-center space-x-2">
            {steps.map((_, index) => (
              <div
                key={index}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index <= currentStep ? 'bg-blue-primary' : 'bg-slate-soft'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Current Step */}
        <div className="transition-all duration-300 ease-in-out">
          {steps[currentStep]}
        </div>
      </div>
    </div>
  );
};

export default OnboardingFlow;
